import { DateTime } from 'luxon';
import {
  type FC,
  forwardRef,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Overlay, Popover } from 'react-bootstrap';
import Container from 'react-bootstrap/Container';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import CheckRadioIcon from '../../../assets/check-radio-icon.svg';
import DateIcon from '../../../assets/date.svg';
import DropDownIcon from '../../../assets/drop-down-icon.svg';
import DropDownOpenIcon from '../../../assets/drop-down-open.svg';
import UnCheckRadioIcon from '../../../assets/uncheck-radio-icon.svg';
import { monthList } from '../../../constants/generalData';
import { roomsPerHousekeeperKey } from '../../../constants/storageKeys';
import { config } from '../../../modules/config';
import { UserPropertiesContext } from '../../../modules/context/userPropertiesContext';
import {
  useAuth,
  useLoading,
  useNotifications,
  useRequest,
} from '../../../modules/hooks';
import type {
  CheckoutPeriodType,
  CheckoutsReportResponse,
} from '../../../modules/interfaces/reports';
import { endpoints } from '../../../modules/mappers/urls';
import { getApiErrorMessage } from '../../../modules/utils/transform';
import Header from '../../Header';
import './styles/styles.css';

const DEFAULT_ROOMS_PER_HOUSEKEEPER = 5;

const PERIOD_OPTIONS: { value: CheckoutPeriodType; label: string }[] = [
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'year', label: 'Year' },
];

interface WeekPickerInputProps {
  /**
   * Deliberately NOT named `value`: react-datepicker clones `customInput` and
   * injects its own `value` (the selected day, formatted MM/dd/yyyy), which would
   * silently overwrite anything we pass under that name. Our week-range label
   * survives only because this prop has a name react-datepicker does not use.
   */
  label?: string;
  onClick?: () => void;
}

const WeekPickerInput = forwardRef<HTMLDivElement, WeekPickerInputProps>(
  ({ label, onClick }, ref) => (
    <div
      ref={ref}
      onClick={onClick}
      className="date-rage-picker-text-container"
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') onClick?.();
      }}
    >
      <img src={DateIcon} alt="" />
      <div className="category-text-style">{label}</div>
      <img className="drop-down-icon-style" src={DropDownIcon} alt="" />
    </div>
  ),
);
WeekPickerInput.displayName = 'WeekPickerInput';

function getStoredRoomsPerHousekeeper(propertyId: string | undefined): number {
  if (!propertyId) return DEFAULT_ROOMS_PER_HOUSEKEEPER;
  const stored = localStorage.getItem(roomsPerHousekeeperKey(propertyId));
  if (!stored) return DEFAULT_ROOMS_PER_HOUSEKEEPER;
  const parsed = Number(stored);
  if (!Number.isFinite(parsed) || parsed < 1) return DEFAULT_ROOMS_PER_HOUSEKEEPER;
  return Math.floor(parsed);
}

function getHousekeepersNeeded(checkouts: number, roomsPerHousekeeper: number): number {
  if (checkouts === 0) return 0;
  return Math.ceil(checkouts / roomsPerHousekeeper);
}

function getHousekeeperShiftsWorked(
  dailyCheckouts: number[],
  roomsPerHousekeeper: number,
): number {
  return dailyCheckouts.reduce(
    (sum, checkouts) => sum + getHousekeepersNeeded(checkouts, roomsPerHousekeeper),
    0,
  );
}

/**
 * "Today" at the property, not in the admin's browser. Someone checking the
 * schedule from another timezone should still land on the property's current
 * week/month.
 */
function propertyToday(): DateTime {
  return DateTime.local().setZone(config.lodgingLocationTimezone);
}

/**
 * Every date on this screen is a plain calendar date (`YYYY-MM-DD`) — the day a
 * guest checks out. It is NOT an instant in time, so it must never be parsed into
 * one zone and rendered in another.
 *
 * The original code parsed with `{ zone: 'utc' }` and handed the result to
 * react-datepicker, which renders in the browser's zone. West of Greenwich, UTC
 * midnight is the *previous* evening: the picker displayed "08/02/2026" while the
 * table under it listed Mon Aug 3 – Sun Aug 9. Parsing zone-free keeps the date the
 * user picked identical to the one we send and the one we render.
 */
function getForecastYearList(): string[] {
  const currentYear = propertyToday().year;
  return Array.from({ length: 8 }, (_, index) => String(currentYear - 2 + index));
}

function getMondayOfWeek(dateIso: string): string {
  const day = DateTime.fromISO(dateIso);
  if (!day.isValid) {
    const today = propertyToday();
    return today.minus({ days: today.weekday - 1 }).toISODate() as string;
  }
  return day.minus({ days: day.weekday - 1 }).toISODate() as string;
}

function formatWeekLabel(weekStartIso: string): string {
  const start = DateTime.fromISO(weekStartIso);
  const end = start.plus({ days: 6 });
  return `${start.toFormat('MMM d')} – ${end.toFormat('MMM d, yyyy')}`;
}

function formatDailyRowLabel(period: string, fallbackLabel: string): string {
  // period is YYYY-MM-DD for week/month rows
  const day = DateTime.fromISO(period);
  if (!day.isValid || period.length !== 10) return fallbackLabel;
  return day.toFormat('ccc, MMM d, yyyy');
}

export const HousekeeperSchedule: FC = () => {
  const { credentialsInfo } = useAuth()!;
  const { selectedProperty } = useContext(UserPropertiesContext)!;
  const { setLoading } = useLoading()!;
  const { setSimpleToasts } = useNotifications()!;

  const now = propertyToday();
  const [periodType, setPeriodType] = useState<CheckoutPeriodType>('month');
  const [periodDraft, setPeriodDraft] = useState<CheckoutPeriodType>('month');
  const [year, setYear] = useState(String(now.year));
  const [yearDraft, setYearDraft] = useState(String(now.year));
  const [month, setMonth] = useState(monthList[now.month - 1]);
  const [monthDraft, setMonthDraft] = useState(monthList[now.month - 1]);
  const [weekDate, setWeekDate] = useState(() =>
    getMondayOfWeek(now.toISODate() as string),
  );
  const propertyId = selectedProperty?.property.uuid;
  const [roomsPerHousekeeper, setRoomsPerHousekeeper] = useState(() =>
    getStoredRoomsPerHousekeeper(propertyId),
  );

  const [periodShow, setPeriodShow] = useState(false);
  const [monthShow, setMonthShow] = useState(false);
  const [yearShow, setYearShow] = useState(false);

  const periodTarget = useRef<HTMLDivElement>(null);
  const monthTarget = useRef<HTMLDivElement>(null);
  const yearTarget = useRef<HTMLDivElement>(null);

  const yearOptions = useMemo(() => getForecastYearList(), []);

  const periodLabel =
    PERIOD_OPTIONS.find((option) => option.value === periodType)?.label ?? 'Month';

  const getCheckoutsUrl = () => {
    const weekStart = getMondayOfWeek(weekDate);
    const params = new URLSearchParams({
      periodType,
      year: periodType === 'week' ? weekStart.slice(0, 4) : year,
    });

    if (periodType === 'month') {
      const monthNumber = monthList.indexOf(month) + 1;
      params.set('month', String(monthNumber));
    }

    if (periodType === 'week') {
      params.set('weekStart', weekStart);
    }

    return `${endpoints.REPORTS}/checkouts?${params.toString()}`;
  };

  const [{ data: checkoutsResult, loading, error }] = useRequest<CheckoutsReportResponse>(
    getCheckoutsUrl(),
    'get',
    {
      authToken: credentialsInfo?.token,
      propertyId: selectedProperty?.property.uuid || undefined,
    },
  );

  useEffect(() => {
    setLoading(loading);
  }, [loading, setLoading]);

  // Without this the empty state doubles as the error state: a failed request
  // renders "No checkout data for this period", which reads as "zero checkouts"
  // and quietly tells the user something false.
  useEffect(() => {
    if (!error) return;
    setSimpleToasts({
      type: 'danger',
      message: getApiErrorMessage(error),
      show: true,
    });
  }, [error, setSimpleToasts]);

  // Load the divisor belonging to the newly selected property. Persisting happens in
  // onRoomsPerHousekeeperChange rather than in an effect on purpose: an effect keyed
  // on propertyId would fire with the *previous* property's number still in state and
  // write it under the new property's key, corrupting the value we just loaded.
  useEffect(() => {
    setRoomsPerHousekeeper(getStoredRoomsPerHousekeeper(propertyId));
  }, [propertyId]);

  const report = checkoutsResult?.result;
  const isYearView = report?.periodType === 'year';

  const summaryHousekeepers = report
    ? isYearView
      ? report.rows.reduce(
          (sum, row) =>
            sum +
            getHousekeeperShiftsWorked(row.dailyCheckouts ?? [], roomsPerHousekeeper),
          0,
        )
      : getHousekeepersNeeded(report.summary.peakCheckouts, roomsPerHousekeeper)
    : 0;

  const onRoomsPerHousekeeperChange = (value: string) => {
    const parsed = Number(value);
    const next = !Number.isFinite(parsed) || parsed < 1 ? 1 : Math.floor(parsed);
    setRoomsPerHousekeeper(next);
    if (propertyId) {
      localStorage.setItem(roomsPerHousekeeperKey(propertyId), String(next));
    }
  };

  const renderOptionItem = (label: string, isSelected: boolean, onSelect: () => void) => (
    <div
      className="drop-down-status-item"
      style={{ backgroundColor: isSelected ? '#F6F6F6' : '#FFFFFF' }}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') onSelect();
      }}
      role="option"
      aria-selected={isSelected}
      tabIndex={0}
    >
      <img
        className="checkbox-icon"
        src={isSelected ? CheckRadioIcon : UnCheckRadioIcon}
        alt=""
      />
      <div style={{ textTransform: 'none' }}>{label}</div>
    </div>
  );

  return (
    <Container fluid className="article-page-container p-0">
      <Header title="Housekeeper Schedule" />

      <div className="artical-text-main-container">
        <div className="artical-dropdown-container">
          <div
            ref={periodTarget}
            onClick={() => {
              setPeriodDraft(periodType);
              setPeriodShow(!periodShow);
            }}
            className="category-text-container"
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                setPeriodDraft(periodType);
                setPeriodShow(!periodShow);
              }
            }}
          >
            <div className="category-text-style">
              <div className="view-text-style">View: </div>
              {periodLabel}
            </div>
            <img src={periodShow ? DropDownOpenIcon : DropDownIcon} alt="" />
          </div>

          {periodType === 'week' && (
            <DatePicker
              selected={DateTime.fromISO(weekDate).toJSDate()}
              onChange={(date: Date | null) => {
                if (!date) return;
                const iso = DateTime.fromJSDate(date).toISODate();
                if (iso) setWeekDate(getMondayOfWeek(iso));
              }}
              customInput={<WeekPickerInput label={formatWeekLabel(weekDate)} />}
              calendarStartDay={1}
              popperPlacement="bottom-start"
            />
          )}

          {periodType === 'month' && (
            <div
              ref={monthTarget}
              onClick={() => {
                setMonthDraft(month);
                setMonthShow(!monthShow);
              }}
              className="date-rage-picker-text-container"
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  setMonthDraft(month);
                  setMonthShow(!monthShow);
                }
              }}
            >
              <img src={DateIcon} alt="" />
              <div className="category-text-style">{month}</div>
              <img
                className="drop-down-icon-style"
                src={monthShow ? DropDownOpenIcon : DropDownIcon}
                alt=""
              />
            </div>
          )}

          {(periodType === 'month' || periodType === 'year') && (
            <div
              ref={yearTarget}
              onClick={() => {
                setYearDraft(year);
                setYearShow(!yearShow);
              }}
              className="date-rage-picker-text-container"
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  setYearDraft(year);
                  setYearShow(!yearShow);
                }
              }}
            >
              <div className="category-text-style">{year}</div>
              <img
                className="drop-down-icon-style"
                src={yearShow ? DropDownOpenIcon : DropDownIcon}
                alt=""
              />
            </div>
          )}

          <div className="rooms-per-housekeeper-control">
            <div className="view-text-style">Rooms / housekeeper</div>
            <input
              className="rooms-per-housekeeper-input"
              type="number"
              min={1}
              step={1}
              value={roomsPerHousekeeper}
              onChange={(event) => onRoomsPerHousekeeperChange(event.target.value)}
            />
          </div>
        </div>

        {report && (
          <div className="housekeeper-schedule-summary">
            <span>
              Total checkouts: <strong>{report.summary.totalCheckouts}</strong>
            </span>
            <span>
              {isYearView ? 'Housekeeper shifts worked' : 'Peak housekeepers needed'}:{' '}
              <strong>{summaryHousekeepers}</strong>
            </span>
          </div>
        )}
      </div>

      <Overlay
        onHide={() => setPeriodShow(false)}
        target={periodTarget.current}
        rootClose
        show={periodShow}
        placement="bottom"
      >
        <Popover id="housekeeper-period-popover">
          <Popover.Body className="property_overlay">
            <div className="drop-down-container-status">
              {PERIOD_OPTIONS.map((option) =>
                renderOptionItem(option.label, periodDraft === option.value, () =>
                  setPeriodDraft(option.value),
                ),
              )}
              <div className="apply-and-cancle-container">
                <div
                  onClick={() => {
                    setPeriodDraft(periodType);
                    setPeriodShow(false);
                  }}
                  className="cancle-text-container"
                >
                  Cancel
                </div>
                <div
                  onClick={() => {
                    setPeriodType(periodDraft);
                    setPeriodShow(false);
                  }}
                  className="apply-text-container"
                >
                  Apply
                </div>
              </div>
            </div>
          </Popover.Body>
        </Popover>
      </Overlay>

      <Overlay
        onHide={() => setMonthShow(false)}
        target={monthTarget.current}
        rootClose
        show={monthShow}
        placement="bottom"
      >
        <Popover id="housekeeper-month-popover">
          <Popover.Body className="property_overlay">
            <div className="drop-down-container-status">
              {monthList.map((monthName) =>
                renderOptionItem(monthName, monthDraft === monthName, () =>
                  setMonthDraft(monthName),
                ),
              )}
              <div className="apply-and-cancle-container">
                <div
                  onClick={() => {
                    setMonthDraft(month);
                    setMonthShow(false);
                  }}
                  className="cancle-text-container"
                >
                  Cancel
                </div>
                <div
                  onClick={() => {
                    setMonth(monthDraft);
                    setMonthShow(false);
                  }}
                  className="apply-text-container"
                >
                  Apply
                </div>
              </div>
            </div>
          </Popover.Body>
        </Popover>
      </Overlay>

      <Overlay
        onHide={() => setYearShow(false)}
        target={yearTarget.current}
        rootClose
        show={yearShow}
        placement="bottom"
      >
        <Popover id="housekeeper-year-popover">
          <Popover.Body className="property_overlay">
            <div className="drop-down-container-status">
              {yearOptions.map((yearOption) =>
                renderOptionItem(yearOption, yearDraft === yearOption, () =>
                  setYearDraft(yearOption),
                ),
              )}
              <div className="apply-and-cancle-container">
                <div
                  onClick={() => {
                    setYearDraft(year);
                    setYearShow(false);
                  }}
                  className="cancle-text-container"
                >
                  Cancel
                </div>
                <div
                  onClick={() => {
                    setYear(yearDraft);
                    setYearShow(false);
                  }}
                  className="apply-text-container"
                >
                  Apply
                </div>
              </div>
            </div>
          </Popover.Body>
        </Popover>
      </Overlay>

      <div className="housekeeper-schedule-table-wrap">
        <table className="housekeeper-schedule-table">
          <thead>
            <tr>
              <th>{isYearView ? 'Month' : 'Date'}</th>
              <th>{isYearView ? 'Total checkouts' : 'Checkouts'}</th>
              <th>{isYearView ? 'Housekeeper shifts worked' : 'Housekeepers needed'}</th>
            </tr>
          </thead>
          <tbody>
            {report?.rows.map((row) => {
              const housekeepersOrShifts = isYearView
                ? getHousekeeperShiftsWorked(
                    row.dailyCheckouts ?? [],
                    roomsPerHousekeeper,
                  )
                : getHousekeepersNeeded(row.checkouts, roomsPerHousekeeper);
              return (
                <tr key={row.period}>
                  <td>
                    {isYearView ? row.label : formatDailyRowLabel(row.period, row.label)}
                  </td>
                  <td>{row.checkouts}</td>
                  <td>{housekeepersOrShifts}</td>
                </tr>
              );
            })}
            {!loading && (!report || report.rows.length === 0) && (
              <tr>
                <td colSpan={3} className="empty-state-cell">
                  No checkout data for this period.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Container>
  );
};
